import React, { createContext, useContext, useState, ReactNode } from 'react';
import { JsonBuilderServiceQuery } from '../../../services/query';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import EmailTemplateBuilderCommand from '../../../services/command/emailTemplateBuilder/EmailTemplateBuilderCommand.services';

interface JsonTemplateContextType {
    filteredDatas: any[];
    allRowCount: number;
    currentPage: number;
    searchKeyword: string;
    setCurrentPage: (page: number) => void;
    setSearchKeyword: (keyword: string) => void;
    getTemplates: (req?: any) => Promise<void>;
    handleDeleteClick: (sectionId: any) => void;
    handleSearch: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleKeyPress: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    handleFilter: () => void;
    getTemplateById: (id: number) => Promise<any>;
}

const JsonTemplateContext = createContext<JsonTemplateContextType | undefined>(undefined);

export const JsonTemplateProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { t } = useTranslation();
    const [filteredDatas, setFilteredDatas] = useState<any[]>([]);
    const [allRowCount, setAllRowCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchKeyword, setSearchKeyword] = useState('');

    const getTemplates = async (req?: any) => {
        if (!req) {
            req = {
                isActive: true,
                startDate: '',
                endDate: '',
                searchText: '',
            };
        }
        try {
            const res = await JsonBuilderServiceQuery.GetTemplatesData(req);
            if (res.model && Array.isArray(res.model)) {
                setAllRowCount(res.totalPages);
                setFilteredDatas(res.model);
            } else {
                setAllRowCount(0);
                setFilteredDatas([]);
                Swal.fire({
                    icon: 'info',
                    title: t('general.noDataFound'),
                    showConfirmButton: false,
                    timer: 1500,
                });
            }
        } catch (error) {
            console.error('Error fetching templates:', error);
            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('general.somethingWentWrong'),
            });
        }
    };

    const handleDeleteClick = async (sectionId: any) => {
        const result = await Swal.fire({
            icon: 'warning',
            title: t('jsonTemplateBuilder.deleteSectionTitle'),
            text: t('jsonTemplateBuilder.deleteSectionConfirmation'),
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('general.yes'),
            cancelButtonText: t('general.no'),
        });

        if (!result.isConfirmed) {
            return;
        }

        const req = { sectionId: sectionId };
        await EmailTemplateBuilderCommand.DeleteSection(req);
        await Swal.fire({
            icon: 'success',
            title: t('general.deleted'),
            text: t('jsonTemplateBuilder.sectionDeleted'),
        });
        getTemplates();
    };

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchKeyword(event.target.value);
    };

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            getTemplates({
                isActive: true,
                startDate: '',
                endDate: '',
                searchText: searchKeyword,
            });
            setCurrentPage(1);
        }
    };

    const handleFilter = () => {
        getTemplates({
            isActive: true,
            startDate: '',
            endDate: '',
            searchText: searchKeyword,
        });
        setCurrentPage(1);
    };

    const getTemplateById = async (id: number) => {
        try {
            const response = await JsonBuilderServiceQuery.GetTemplateById(id);
            if (response && response.model) {
                return response.model;
            }
            throw new Error('Template not found');
        } catch (error) {
            console.error('Error fetching template details:', error);
            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('general.somethingWentWrong'),
            });
            throw error;
        }
    };

    const value = {
        filteredDatas,
        allRowCount,
        currentPage,
        searchKeyword,
        setCurrentPage,
        setSearchKeyword,
        getTemplates,
        handleDeleteClick,
        handleSearch,
        handleKeyPress,
        handleFilter,
        getTemplateById,
    };

    return <JsonTemplateContext.Provider value={value}>{children}</JsonTemplateContext.Provider>;
};

export const useJsonTemplate = () => {
    const context = useContext(JsonTemplateContext);
    if (context === undefined) {
        throw new Error('useJsonTemplate must be used within a JsonTemplateProvider');
    }
    return context;
};
